import { useEffect, useCallback, useRef } from 'react'

/**
 * Attach an event listener to the window, and remove it on cleanup
 */
export function useWindowEventListener({
    eventName,
    handler,
    onMountCallback,
}: {
    eventName: string
    handler: (e: Event) => void
    // optionally run a callback on mount
    onMountCallback?: () => void
}) {
    // Note: `element` could potentially be any DOM element, but we don't currently foresee a need to use this hook
    // with anything other than the window element, since other events can be handled with React event handlers.
    // If we have a future use case for attaching events to other DOM elements, this could be changed into a prop.
    const element: EventTarget = window
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const savedHandler = useCallback(handler, [])
    const mounted = useRef(false)
    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }
        if (onMountCallback && !mounted.current) {
            onMountCallback()
            mounted.current = true
        }
        // Create event listener that calls handler function
        const eventListener = (event: Event) => savedHandler(event)
        // Add event listener
        element.addEventListener(eventName, eventListener)
        // Remove event listener on cleanup
        return () => {
            element.removeEventListener(eventName, eventListener)
        }
    }, [eventName, element, savedHandler, onMountCallback])
}
